import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggingService } from '@services/logging.service';
import { Router } from '@angular/router';
import { StudyComments } from '@models/studyComments.model';
import { SubsetModel } from '@models/subset.model';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class SubsetService {
  savedComments: StudyComments[] = [];
  errorMessage = '';
  constructor(private http: HttpClient, private router: Router, private logger: LoggingService) {}

  public async getAllBDMatrixSubset(searchStrategyID: number): Promise<SubsetModel[]> {
    this.logger.info(this.constructor.name, 'getAllBDMatrixSubset', 'Start');

    if (!searchStrategyID) return null;

    const studyAllSubsets: SubsetModel[] = <SubsetModel[]>await firstValueFrom(
      this.http.get<SubsetModel[]>(`/api/BDMatrix/GetAllBDMatrixSubset/${searchStrategyID}`)
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'getAllBDMatrixSubset', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });

    return studyAllSubsets;
  }

  public async addUpdateBDMatrixSubset(subset: SubsetModel): Promise<SubsetModel[]> {
    this.logger.info(this.constructor.name, 'addUpdateBDMatrixSubset', 'Start', subset);
    this.errorMessage = '';

    let savedSubset: SubsetModel[] = [];
    savedSubset = <SubsetModel[]>await firstValueFrom(this.http.post<SubsetModel[]>('/api/BDMatrix/AddUpdateBDMatrixSubset', subset)).catch((e) => {
      if (e.error !== undefined && e.error != null && e.error.error !== undefined) {
        this.errorMessage = e.error.error;
      }
      this.logger.httpError(this.constructor.name, 'addUpdateBDMatrixSubset', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });

    return savedSubset;
  }

  public async removeBDMatrixSubset(subset: SubsetModel): Promise<void> {
    this.logger.info(this.constructor.name, 'removeBDMatrixSubset', 'Start', subset);
    this.errorMessage = '';

    await firstValueFrom(this.http.post('/api/BDMatrix/RemoveBDMatrixSubset', subset)).catch((e) => {
      if (e.error !== undefined && e.error != null && e.error.error !== undefined) {
        this.errorMessage = e.error.error;
      }
      this.logger.httpError(this.constructor.name, 'removeBDMatrixSubset', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });
  }

  public async addUpdateInternetReviewSubset(subset: SubsetModel): Promise<SubsetModel[]> {
    this.logger.info(this.constructor.name, 'addUpdateInternetReviewSubset', 'Start', subset);
    this.errorMessage = '';

    let savedSubset: SubsetModel[] = [];
    savedSubset = <SubsetModel[]>await firstValueFrom(
      this.http.post<SubsetModel[]>('/api/InternetReview/AddUpdateInternetReviewSubset', subset)
    ).catch((e) => {
      if (e.error !== undefined && e.error != null && e.error.error !== undefined) {
        this.errorMessage = e.error.error;
      }
      this.logger.httpError(this.constructor.name, 'addUpdateInternetReviewSubset', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });

    return savedSubset;
  }

  public async removeInternetReviewSubset(subset: SubsetModel): Promise<void> {
    this.logger.info(this.constructor.name, 'removeInternetReviewSubset', 'Start', subset);
    this.errorMessage = '';

    await firstValueFrom(this.http.post('/api/InternetReview/RemoveInternetReviewSubset', subset)).catch((e) => {
      if (e.error !== undefined && e.error != null && e.error.error !== undefined) {
        this.errorMessage = e.error.error;
      }
      this.logger.httpError(this.constructor.name, 'removeInternetReviewSubset', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });
  }

  public async addUpdateFinancialReviewSubset(selected: SubsetModel): Promise<SubsetModel[]> {
    this.logger.info(this.constructor.name, 'addUpdateFinancialReviewSubset', 'Start', selected);
    const saveResult: SubsetModel[] = <SubsetModel[]>await firstValueFrom(
      this.http.post<SubsetModel[]>('/api/FinancialReview/AddUpdateFinancialReviewSubset', selected)
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'addUpdateFinancialReviewSubset', e);
    });
    return saveResult;
  }

  public async removeFinancialReviewSubset(subset: SubsetModel): Promise<void> {
    this.logger.info(this.constructor.name, 'removeFinancialReviewSubset', 'Start', subset);
    this.errorMessage = '';

    await firstValueFrom(this.http.post('/api/FinancialReview/RemoveFinancialReviewSubset', subset)).catch((e) => {
      if (e.error !== undefined && e.error != null && e.error.error !== undefined) {
        this.errorMessage = e.error.error;
      }
      this.logger.httpError(this.constructor.name, 'removeFinancialReviewSubset', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });
  }

  public async addUpdateMasterMatrixSubset(selected: SubsetModel): Promise<SubsetModel[]> {
    this.logger.info(this.constructor.name, 'addUpdateMasterMatrixSubset', 'Start', selected);
    const saveResult: SubsetModel[] = <SubsetModel[]>await firstValueFrom(
      this.http.post<SubsetModel[]>('/api/MasterMatrix/AddUpdateMasterMatrixSubset', selected)
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'addUpdateMasterMatrixSubset', e);
    });
    return saveResult;
  }

  public async removeMasterMatrixSubset(subset: SubsetModel): Promise<void> {
    this.logger.info(this.constructor.name, 'removeMasterMatrixSubset', 'Start', subset);
    this.errorMessage = '';

    await firstValueFrom(this.http.post('/api/MasterMatrix/RemoveMasterMatrixSubset', subset)).catch((e) => {
      if (e.error !== undefined && e.error != null && e.error.error !== undefined) {
        this.errorMessage = e.error.error;
      }
      this.logger.httpError(this.constructor.name, 'removeMasterMatrixSubset', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });
  }

  public async isSubsetNameDuplicate(subset: SubsetModel): Promise<boolean> {
    this.logger.info(this.constructor.name, 'isSubsetNameDuplicate', 'Start', subset);
    this.errorMessage = '';

    let isDuplicateSubsetName = false;
    isDuplicateSubsetName = <boolean>await firstValueFrom(this.http.post<boolean>('/api/BDMatrix/IsDuplicateSubsetName', subset)).catch((e) => {
      if (e.error !== undefined && e.error != null && e.error.error !== undefined) {
        this.errorMessage = e.error.error;
      }
      this.logger.httpError(this.constructor.name, 'isSubsetNameDuplicate', e);
      if (e.status === 401) {
        this.router.navigate(['nostudyaccess']);
      }
    });

    return isDuplicateSubsetName;
  }
}
